import { useRef } from "react";
import { motion, useMotionValue, useReducedMotion, useSpring } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useTransition } from "@/context/UIContext";
import { useFinePointer } from "@/hooks/hooks";

export const MagneticButton = ({ children, to, onClick, type = "button", disabled = false, className = "", testId }) => {
    const { go } = useTransition();
    const fine = useFinePointer();
    const reduced = useReducedMotion();
    const ref = useRef(null);
    const x = useMotionValue(0);
    const y = useMotionValue(0);
    const sx = useSpring(x, { stiffness: 160, damping: 16, mass: 0.4 });
    const sy = useSpring(y, { stiffness: 160, damping: 16, mass: 0.4 });

    return (
        <motion.button
            ref={ref}
            type={type}
            disabled={disabled}
            data-testid={testId || "primary-button"}
            data-cursor="button"
            onClick={(e) => {
                if (disabled) return;
                if (to && go) return go(to);
                if (onClick) onClick(e);
            }}
            onMouseMove={(e) => {
                if (!fine || reduced) return;
                const r = ref.current.getBoundingClientRect();
                x.set((e.clientX - r.left - r.width / 2) * 0.24);
                y.set((e.clientY - r.top - r.height / 2) * 0.34);
            }}
            onMouseLeave={() => {
                x.set(0);
                y.set(0);
            }}
            style={{ x: sx, y: sy }}
            whileHover={reduced ? undefined : { scale: 1.04 }}
            whileTap={{ scale: 0.97 }}
            className={`group relative inline-flex h-12 items-center gap-3 overflow-hidden rounded-full bg-electric px-8 font-display text-[13px] font-medium tracking-wider text-white shadow-glow transition-shadow duration-300 hover:shadow-glow-lg disabled:cursor-wait disabled:opacity-60 ${className}`}
        >
            <span className="absolute inset-0 translate-y-full rounded-full bg-electric-700 transition-transform duration-500 ease-premium group-hover:translate-y-0" />
            <span className="pointer-events-none absolute inset-y-0 left-0 w-1/3 -translate-x-[130%] skew-x-[-18deg] bg-white/30 opacity-0 group-hover:animate-sheen group-hover:opacity-100" />
            <span className="relative z-10 transition-transform duration-300">{children}</span>
            <ArrowRight className="relative z-10 h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" />
        </motion.button>
    );
};

export const SecondaryButton = ({ children, to, onClick, className = "", testId }) => {
    const { go } = useTransition();
    return (
        <motion.button
            data-testid={testId || "secondary-button"}
            data-cursor="button"
            onClick={(e) => {
                if (to && go) return go(to);
                if (onClick) onClick(e);
            }}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            className={`group relative inline-flex h-12 items-center gap-2 overflow-hidden rounded-full border border-electric/40 px-8 font-display text-[13px] font-medium tracking-wider text-ice transition-colors duration-300 hover:border-electric hover:text-electric ${className}`}
        >
            <span className="absolute inset-0 scale-x-0 rounded-full bg-electric/10 transition-transform duration-500 ease-premium group-hover:scale-x-100" />
            <span className="relative z-10 grid w-0 overflow-hidden transition-all duration-300 group-hover:w-5">
                <ArrowRight className="h-4 w-4" />
            </span>
            <span className="relative z-10">{children}</span>
        </motion.button>
    );
};
